/**
 * "Share usage data" with "Delete my usage data" (docs/APP-EVENTS.md): Settings → More in a business, More in a home (homes too
 * since 2026-10-07). This browser remembers the choice.
 */
import { useState } from "react";
import { optedOut, setUsageChoice, usageChoice } from "../lib/appEvents.ts";
import { useApi } from "../session.tsx";
import { USAGE_CHANGED } from "./AppEventsBridge.tsx";
import { Button, Card, Section } from "./ui.tsx";

/**
 * "Share usage data" (docs/APP-EVENTS.md): which screens and buttons this person uses, for this business only. Off until the person
 * turns it on (owner, 2026-10-09: no analytics before consent); the switch also records the usage_data consent on the server, which
 * keeps nothing without it; this browser remembers the choice; Do Not Track or Global Privacy Control turns it off whatever the choice. "Delete my
 * usage data" removes everything sent from this person, on every phone and computer.
 */
export function UsageDataCard() {
  const api = useApi();
  const dnt = optedOut();
  const [on, setOn] = useState(() => !dnt && (usageChoice() ?? false));
  const [msg, setMsg] = useState<string>();
  const [busy, setBusy] = useState(false);
  const flip = (v: boolean) => {
    setUsageChoice(v); setOn(v); setMsg(v ? "Usage data is on. Thank you." : "Usage data is off. Nothing more is sent from this browser."); window.dispatchEvent(new Event(USAGE_CHANGED));
    void api.setConsent("usage_data", v).catch(() => undefined); // the server keeps events only after this yes
  };
  const del = async () => {
    setBusy(true);
    try { const r = await api.deleteMyAppEvents(); setMsg(r.removed ? `Deleted. ${r.removed === 1 ? "1 record" : `${r.removed} records`} of your taps and screens are gone.` : "Nothing to delete."); }
    catch { setMsg("Couldn't reach CleanCleaver. Try again in a minute."); }
    finally { setBusy(false); }
  };
  return (
    <Section id="usage-data" title="Share usage data" kicker="Privacy">
      <Card className="stack">
        <p className="small">Which screens you open and which buttons you tap, so we fix what slows you down. When you open a buy link, we count the tap, and a day later we may ask once if you got it. Never what you type, and nothing from check-ins or sick reports. Off until you turn it on.</p>
        {dnt ? <p className="small" data-testid="usage-dnt">Your browser asks sites not to track you (Do Not Track or Global Privacy Control), so nothing is sent from it.</p> : (
          <label className="check">
            <input type="checkbox" checked={on} data-testid="share-usage" onChange={(e) => flip(e.target.checked)} />
            Share usage data from this browser
          </label>
        )}
        <div className="row"><Button variant="secondary" small busy={busy} data-testid="usage-delete" onClick={() => void del()}>Delete my usage data</Button></div>
        {msg && <p className="small" role="status" data-testid="usage-msg">{msg}</p>}
      </Card>
    </Section>
  );
}

